import { useTranslation } from '@thoughtspot/i18n';
import { Alert, BannerAlertType, } from '@thoughtspot/radiant-react/widgets/alert';
import { Link } from '@thoughtspot/radiant-react/widgets/link';
import { getUserEmail, isSaaSCluster } from '@thoughtspot/session-service';
import React, { useEffect, useState } from 'react';
import { useGetEulaListQuery, useSignEulaMutation, } from '../../../services/system/eula/eula-service';
import { Header, Seperator, WithQueryHandler } from '../common';
import { EulaDataMessage, TranslationsKeys } from './constants';
import styles from './user-agreement.module.scss';
export const UserAgreementContainer = ({ onSuccess, onError, showLoader, hideLoader, }) => {
    const { t } = useTranslation();
    const [error, setError] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [currentEula, setCurrentEula] = useState(null);
    const [previousEulaLink, setPreviousEulaLink] = useState('');
    const [bannerVisible, setBannerVisible] = useState(true);
    const [acceptanceRequired, setAcceptanceRequired] = useState(true);
    const [isAcceptBtnDisabled, setIsAcceptBtnDisabled] = useState(false);
    const [hasSignedEula, setHasSignedEula] = useState(false);
    const isSaasCluster = isSaaSCluster();
    const handleSignSuccess = (result) => {
        if (result?.Data?.Message === EulaDataMessage.EULA_ALREADY_SIGNED) {
            onSuccess(t(TranslationsKeys.ALREADY_SIGN_MESSAGE));
        }
        else if (result?.Data?.Message === EulaDataMessage.EULA_SIGNED_SUCCESSFULLY) {
            onSuccess(t(TranslationsKeys.DIALOG_SIGN_SUCCESS));
        }
        setHasSignedEula(true);
        setBannerVisible(false);
        const globalAlert = document.querySelector('[data-testid="EulaBannerComponentId"]');
        if (globalAlert) {
            globalAlert.remove();
        }
    };
    const handleSignError = (error) => {
        onError(error.message);
        setError(true);
        setBannerVisible(true);
        setIsAcceptBtnDisabled(false);
    };
    const { data: eulaListData, loading: eulaListLoading, error: eulaListError, refetch, } = useGetEulaListQuery();
    const [signEulaMutation] = useSignEulaMutation(getUserEmail(), handleSignSuccess, handleSignError);
    useEffect(() => {
        if (!eulaListLoading && eulaListData?.Data?.eula) {
            const eula = eulaListData.Data.eula;
            const latestEula = eula[eula.length - 1];
            const notSignedOffline = eulaListData.Data.acceptance_required;
            if (notSignedOffline && eula.length > 1) {
                const previousEula = eula[eula.length - 2];
                if (previousEula.is_signed && !latestEula.is_signed) {
                    setPreviousEulaLink(previousEula.file_path);
                }
            }
            setCurrentEula(latestEula);
            setAcceptanceRequired(notSignedOffline);
            if (!hasSignedEula && !latestEula.is_signed && notSignedOffline) {
                setBannerVisible(true);
            }
            else {
                setBannerVisible(false);
            }
            hideLoader();
        }
        else if (eulaListError) {
            setError(true);
            onError(eulaListError.message);
            hideLoader();
            setBannerVisible(false);
        }
        setIsLoading(eulaListLoading);
    }, [eulaListData, eulaListError, eulaListLoading, hideLoader, onError]);
    useEffect(() => {
        const handler = () => {
            refetch();
        };
        window.addEventListener('eula-signed', handler);
        return () => window.removeEventListener('eula-signed', handler);
    }, [refetch]);
    const signingEula = async () => {
        setIsAcceptBtnDisabled(true);
        const userEmail = getUserEmail();
        if (!userEmail) {
            onSuccess(t(TranslationsKeys.DIALOG_EMAIL_LABEL));
            return;
        }
        try {
            showLoader();
            setIsLoading(true);
            await signEulaMutation({
                variables: { emailId: userEmail },
            });
        }
        catch (err) {
            onError(err.message);
            setError(true);
        }
        finally {
            setIsLoading(false);
            hideLoader();
        }
    };
    const primaryButton = {
        label: t(TranslationsKeys.SIGN_ACTION),
        onClick: () => signingEula(),
        isDisabled: isAcceptBtnDisabled,
    };
    const eulaAlertSecondPart = t(TranslationsKeys.MESSAGE_ADMIN_SAAS_SECOND_PART, {
        days: currentEula?.grace_period,
    });
    const descriptionMsg = (React.createElement("span", null,
        t(currentEula?.is_signed || hasSignedEula
            ? TranslationsKeys.USER_AGREEMENT_DESCRIPTION_FIRST_PART_EULA_SIGNED
            : TranslationsKeys.USER_AGREEMENT_DESCRIPTION_FIRST_PART_EULA_NOT_SIGNED),
        ' ',
        React.createElement(Link, { href: previousEulaLink || currentEula?.file_path, target: "_blank", rel: "noreferrer" }, t(TranslationsKeys.USER_AGREEMENT_DESCRIPTION_LINK)),
        ' ',
        t(TranslationsKeys.USER_AGREEMENT_DESCRIPTION_SECOND_PART)));
    const bannerMsg = (React.createElement("span", null,
        t(TranslationsKeys.MESSAGE_ADMIN_SAAS_FIRST_PART),
        ' ',
        React.createElement(Link, { href: currentEula?.file_path, target: "_blank", rel: "noreferrer" }, t(TranslationsKeys.MESSAGE_ADMIN_SAAS_LINK)),
        ' ',
        eulaAlertSecondPart));
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: styles.headerWrapper },
            React.createElement(Header, { title: t(TranslationsKeys.LEGAL_NAME), titleSize: "large", isFullWidth: true })),
        React.createElement(Seperator, null),
        isSaasCluster && !isLoading && !error && (React.createElement("div", { className: styles.userAgreementContainer, "data-testid": "user-agreement-container" },
            React.createElement("div", { className: styles.headerWrapper },
                React.createElement(Header, { title: t(TranslationsKeys.USER_AGREEMENT_NAME), titleSize: "medium", subTitle: descriptionMsg, isFullWidth: true })),
            acceptanceRequired && !currentEula?.is_signed && (React.createElement("div", { id: "eula-agreement-banner", "data-testid": "eula-banner" },
                React.createElement(Alert.Banner, { isVisible: bannerVisible, message: bannerMsg, type: BannerAlertType.INFO, showCloseButton: false, primaryAction: primaryButton, className: styles.infoBanner })))))));
};
export const UserAgreement = () => (React.createElement(WithQueryHandler, null,
    React.createElement(UserAgreementContainer, null)));
//# sourceMappingURL=user-agreement.container.js.map